import { Badge } from "@volca/ui/components/ui/badge";
import { TableCell, TableRow } from "@volca/ui/components/ui/table";
import { cn } from "@volca/ui/lib/utils";
import { splitFlag } from "@volca/ui/trades";
import { useReportCurrency } from "@/profitability/currency";
import { MissingValue } from "@/profitability/missing-value";
import { shortPeriod } from "@/profitability/new-run/run-period";
import { rateColumn } from "@/profitability/records/judgement";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import {
  findReferenced,
  recordName,
  rowLinks,
  referenceRef,
} from "@/profitability/records/objects";
import { RecordValue } from "@/profitability/records/record-value";
import { isAbsent } from "@/profitability/records/record-view/missing-fields";
import { UploadInputButton } from "@/profitability/records/record-view/upload-input-button";
import { FindingStatusMenu } from "./finding-status";
import { causeName, money, rate } from "./labels";
import { Rating } from "./rating";
import { isProfit, useReviewSpec, type ReviewSpec } from "./review-spec";
import { SEVERITY_LABEL, SeverityMark, agentLine } from "./severity-mark";
import type { Finding } from "./types";

/** The record a finding is about, by the id the API gave it. */
export const findingRef = (finding: Finding): RecordRef => ({
  dataset: finding.dataset,
  recordId: finding.record_id,
});

/** A finding's record by name: its title, else its row's name. Never its id. */
export const findingName = (finding: Finding, object?: RecordObject) =>
  finding.title ?? (object ? recordName(object, finding.snapshot) : "");

/** The flag as one sentence. */
export const findingSentence = (finding: Finding) => {
  const { detail } = splitFlag(finding.flag);
  const note = (detail || finding.flag).trim();
  return note ? note.charAt(0).toUpperCase() + note.slice(1) : "";
};

/**
 * Money under the average margin. Only a profit report has one; a shortfall
 * of nothing, or a commission report, stays blank.
 */
export const findingShortfall = (
  spec: ReviewSpec,
  finding: Finding,
): number | null =>
  isProfit(spec) && finding.shortfall != null && finding.shortfall > 0
    ? finding.shortfall
    : null;

/**
 * The record's one short key (a job number, a department, a technician), the
 * way a work order is numbered. A long or composite id stays hidden.
 */
const recordKey = (finding: Finding): string | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(finding.record_id);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length !== 1) return null;
  const raw = parsed[0];
  const value =
    typeof raw === "string" || typeof raw === "number"
      ? String(raw).trim()
      : "";
  if (!value || value.length > 24) return null;
  return value;
};

/**
 * The chip naming what a finding is about, by the record's own kind: "Job
 * 48213", "Department HVAC Service", "Technician Devon Park". Null when the
 * record has no short key.
 */
export const recordKeyLabel = (
  finding: Finding,
  object: RecordObject | undefined,
): string | null => {
  const key = recordKey(finding);
  // Only a number needs its kind beside it; a name ("HVAC Service") is the
  // row's subject on its own.
  return key && /^\d/.test(key) ? `${object?.name ?? "Record"} ${key}` : null;
};

/** The records a finding's row links to: its unit, its technician. */
export const linksOf = (
  finding: Finding,
  object: RecordObject | undefined,
  objects: RecordObject[],
) => rowLinks(object, objects, finding.snapshot);

/**
 * One flagged job as a row of its rule's table. What differs from row to
 * row leads: how much it matters, the job, what the rule found in it, and
 * who did it where (each with its mark). What every row shares, the rule
 * and Volca's read of it, sits once on the frame. The rating and the
 * status stay in the row. The row opens the job; its names and controls
 * act in place.
 */
export const FindingRow = ({
  businessId,
  finding,
  object,
  objects,
  selected,
  cause,
  canUpload = false,
  showPeriod = false,
  onOpen,
  onOpenRecord,
}: {
  businessId: string;
  finding: Finding;
  object: RecordObject | undefined;
  objects: RecordObject[];
  selected: boolean;
  /** The likely cause the rule's group shares, said once on the frame. */
  cause?: string | null;
  /** Offer the upload that fills a record's missing figures. */
  canUpload?: boolean;
  /** The rows come from several periods, so each says which. */
  showPeriod?: boolean;
  onOpen: () => void;
  onOpenRecord: (ref: RecordRef) => void;
}) => {
  const spec = useReviewSpec();
  const currency = useReportCurrency();
  const name = findingName(finding, object) || (object?.name ?? "Record");
  const number = recordKey(finding);
  const keyLabel = recordKeyLabel(finding, object);
  const showName = keyLabel === null || (name !== number && name !== keyLabel);
  const noun = (object?.name ?? "record").toLowerCase();
  const links = linksOf(finding, object, objects);
  const below = findingShortfall(spec, finding);
  const ownCause =
    cause &&
    finding.hypothesis?.cause &&
    finding.hypothesis.cause !== cause &&
    causeName(
      finding.hypothesis.cause,
      finding.hypothesis.cause_name,
      spec.causes,
    );
  const sentence = findingSentence(finding);
  const dates =
    finding.start_date && finding.end_date
      ? { start_date: finding.start_date, end_date: finding.end_date }
      : null;
  const uploads = canUpload && !finding.resolution ? finding.upload_inputs : [];
  // The record's rate; a dash when its figures are missing, nothing when
  // there is none to take (a callback brings in nothing).
  const shownRate = finding.margin === null ? null : rate(finding.margin);
  const rateField = object && rateColumn(object.dataset);
  const rateMissing =
    shownRate === null &&
    !!object &&
    !!rateField &&
    isAbsent(object.dataset, finding.snapshot, rateField);
  const level = finding.severity
    ? SEVERITY_LABEL[finding.severity].toLowerCase()
    : null;
  return (
    <TableRow
      data-state={selected ? "selected" : undefined}
      // Narrow (a phone): the figures and controls drop under the finding
      // instead of squeezing its words to a letter a line, and the row stays
      // one card (no rules between its stacked cells).
      className="relative cursor-pointer @max-lg:grid @max-lg:grid-cols-[auto_minmax(0,1fr)]"
    >
      <TableCell className="w-0 py-3 pe-1 align-top @max-lg:row-span-3 @max-lg:w-auto">
        <button
          type="button"
          aria-label={`Open ${[keyLabel ?? noun, showName ? name : null, level, agentLine(finding.hypothesis)].filter(Boolean).join(", ")}`}
          aria-current={selected || undefined}
          onClick={onOpen}
          className="absolute inset-0 rounded-lg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink"
        />
        <span className="pointer-events-none relative z-10 flex h-5 items-center">
          <SeverityMark
            severity={finding.severity}
            hypothesis={finding.hypothesis}
            onOpen={onOpen}
          />
        </span>
      </TableCell>
      <TableCell className="w-full max-w-0 py-3 whitespace-normal @max-lg:max-w-none @max-lg:border-e! @max-lg:border-b-0! @max-lg:pb-1 @max-lg:[tr:first-child_&]:rounded-se-lg!">
        <span className="flex min-w-0 flex-col gap-1.5">
          <span className="flex min-w-0 items-center gap-2 text-sm leading-5 @max-lg:flex-wrap">
            {keyLabel && (
              <RecordValue
                spec={{ kind: "badge", label: keyLabel }}
                className="shrink-0"
              />
            )}
            <span className="min-w-0 text-pretty [overflow-wrap:anywhere] text-ink">
              {showName && <span className="block font-medium">{name}</span>}
              {sentence}
            </span>
          </span>
          {/* Only the names take the click; the rest of the line opens the row. */}
          <span className="pointer-events-none relative flex min-w-0 flex-wrap items-center gap-1.5 text-sm [&_button]:pointer-events-auto">
            {links.map((link) => (
              <RecordValue
                key={link.key}
                spec={{
                  kind: "record",
                  object: link.target,
                  name: link.name,
                  trade: link.target.icon === "team" ? link.name : undefined,
                  row: findReferenced(link.target, link.value),
                  onOpen: () =>
                    onOpenRecord(referenceRef(link.target, link.value)),
                }}
              />
            ))}
            {showPeriod && finding.start_date && finding.end_date && (
              <span className="text-subtle tabular-nums">
                {shortPeriod({
                  start_date: finding.start_date,
                  end_date: finding.end_date,
                })}
              </span>
            )}
            {ownCause && (
              <Badge variant="outline" size="sm">
                Likely: {ownCause}
              </Badge>
            )}
          </span>
          {uploads.length > 0 && (
            <span className="relative z-10 flex flex-wrap gap-2 pt-1">
              {uploads.map((input) => (
                <UploadInputButton
                  key={input}
                  reportId={finding.report_id}
                  source={input}
                  dates={dates}
                  runId={finding.run_id}
                  variant="outline"
                />
              ))}
            </span>
          )}
        </span>
      </TableCell>
      <TableCell className="w-28 py-3 text-end align-top @max-lg:col-start-2 @max-lg:w-auto @max-lg:border-y-0! @max-lg:border-e! @max-lg:py-0 @max-lg:text-start">
        <span className="flex flex-col items-end gap-1 text-sm tabular-nums @max-lg:flex-row @max-lg:items-baseline @max-lg:gap-3">
          {/* Every row here is short of the average; only a loss is red. */}
          {below !== null ? (
            <span
              className="text-ink"
              title={`Below the average ${spec.metric.label}`}
            >
              −{money(below, currency)}
            </span>
          ) : null}
          {rateMissing && (
            <span className="text-subtle">
              {spec.metric.label} <MissingValue />
            </span>
          )}
          {shownRate !== null && (
            <span
              className={cn("text-subtle", {
                "text-danger": (finding.margin ?? 0) < 0,
              })}
            >
              {/* The report's own label, as written ("GM %"), before the value. */}
              {spec.metric.label} {shownRate}
            </span>
          )}
        </span>
      </TableCell>
      <TableCell className="w-0 py-3 align-top @max-lg:col-start-2 @max-lg:w-auto @max-lg:rounded-se-none! @max-lg:border-t-0! @max-lg:pt-1">
        <span className="pointer-events-none relative flex items-center justify-end gap-2 @max-lg:justify-start [&_button]:pointer-events-auto">
          <Rating
            businessId={businessId}
            subjectType="finding"
            subjectId={finding.id}
            runId={finding.run_id}
            feedback={finding.feedback}
            label={`${noun} ${name}`}
            noun={object ? noun : undefined}
          />
          <FindingStatusMenu
            businessId={businessId}
            finding={finding}
            aligned
          />
        </span>
      </TableCell>
    </TableRow>
  );
};
